/* eslint-disable promise/no-promise-in-callback */
import type { AutoTrackingReservation } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { logger } from '#~/logger/index.js';
import type { Subscription } from 'nats.ws';
import type { Proxy } from '#~/agent/types.js';

/** A class to manage auto tracking reservations for an agent. */
export class AutoTrackingSubscriptionManager {
  readonly #reservationSubscriptions = new Map<string, Subscription>();

  /**
   * Creates a new auto tracking manager.
   *
   * @param conn - The nats connection to use.
   * @param agentSubordinate - The subordinate to sync agent state with.
   */
  public constructor(_proxy: Proxy) {}

  /**
   * Ensures that we have the correct subscriptions for the current reservations. This is called
   * when a mutation is made to the reservation state.
   *
   * @param reservations - The reservations to check.
   */
  public checkSubscriptions(reservations: Record<string, AutoTrackingReservation>): void {
    // Loop over all subscriptions
    for (const [key] of this.#reservationSubscriptions) {
      // If the subscription is not for a reservation, unsubscribe
      if (!reservations[key]) {
        this.#unsubscribeFromReservationUpdates(key);
      }
    }
  }

  /**
   * Unsubscribes for updates to reservations on the reservation channel.
   *
   * @param cameraId - The id of the camera to unsubscribe from.
   */
  #unsubscribeFromReservationUpdates(cameraId: string): void {
    // Get the subscription for the camera
    const sub = this.#reservationSubscriptions.get(cameraId);

    logger.debug('> Unsubscribing from camera', cameraId);

    // If there is a subscription, unsubscribe from it and remove it from the map
    if (sub) {
      sub.unsubscribe();
      this.#reservationSubscriptions.delete(cameraId);
    }
  }
}
